import { useCallback, useEffect, useState } from "react";
import api, { apiErr, fmt2 } from "@/lib/api";
import { fl } from "@/lib/finI18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";

const n = (v) => (Math.abs(v || 0) < 0.005 ? "—" : fmt2(v));

// Unified WHT: detail withheld per tax type, the period-end transfer into Utang PPh Unifikasi, deposits and balance.
export function UnifikasiMasa({ clientId, year, lang }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    if (clientId) api.get(`/reports/unifikasi?client_id=${clientId}&year=${year}&lang=${lang}`).then(({ data }) => setD(data)).catch(() => {});
  }, [clientId, year, lang]);
  useEffect(() => { load(); }, [load]);
  const recalc = async () => {
    setBusy(true);
    try { await api.post("/unifikasi/recalc", { client_id: clientId }); toast.success(fl("uniRecalcDone", lang)); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (!d) return null;
  return (
    <Card className="p-4 space-y-3" data-testid="uni-masa">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-head font-semibold">{fl("uniTitle", lang)} · <span className="num">{d.year}</span></h3>
          <p className="text-xs text-muted-foreground max-w-3xl">{fl("uniHint", lang)} <span className="num">({d.account.code} {d.account.label})</span></p>
        </div>
        <Button variant="outline" size="sm" onClick={recalc} disabled={busy} data-testid="uni-recalc"><RefreshCw className={`h-4 w-4 mr-1 ${busy ? "animate-spin" : ""}`} />{fl("uniRecalc", lang)}</Button>
      </div>
      <div className="overflow-auto rounded-md border border-border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead rowSpan={2}>{fl("uniPeriod", lang)}</TableHead>
              <TableHead colSpan={d.keys.length} className="text-center border-l">{fl("uniWithheld", lang)}</TableHead>
              <TableHead rowSpan={2} className="text-right border-l">{fl("uniMoved", lang)}</TableHead>
              <TableHead rowSpan={2} className="text-right">{fl("uniPaid", lang)}</TableHead>
              <TableHead rowSpan={2} className="text-right">{fl("uniBalance", lang)}</TableHead>
            </TableRow>
            <TableRow>{d.keys.map((k, i) => <TableHead key={k.key} className={`text-right text-[11px] ${i === 0 ? "border-l" : ""}`} title={k.label}>{k.label}</TableHead>)}</TableRow>
          </TableHeader>
          <TableBody>
            {d.rows.map((r) => (
              <TableRow key={r.period} data-testid={`uni-row-${r.period}`} className={r.closed ? "" : "bg-secondary/40"}>
                <TableCell className="num text-xs whitespace-nowrap">{r.period}{!r.closed && <span className="block text-[10px] text-muted-foreground">{fl("uniOpen", lang)}</span>}</TableCell>
                {d.keys.map((k, i) => <TableCell key={k.key} className={`num text-right text-xs ${i === 0 ? "border-l" : ""}`}>{n(r.withheld[k.key])}</TableCell>)}
                <TableCell className="num text-right text-xs font-semibold border-l" data-testid={`uni-moved-${r.period}`}>{n(r.total_moved)}</TableCell>
                <TableCell className="num text-right text-xs" data-testid={`uni-paid-${r.period}`}>{n(r.paid)}</TableCell>
                <TableCell className="num text-right text-xs font-semibold" data-testid={`uni-balance-${r.period}`}>{fmt2(r.balance)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}
